import React, { useContext, useState } from 'react';

import CurrentCustomer from 'context/global-customer-creation/context';
import OnboardingSaveSteps from 'context/onboarding-moral-person-save-steps/context';
import type { DataCurrentCustomerType } from 'context/global-customer-creation/types';

import { formatDate } from 'utils/date';

import {
  saveInvestingExperience,
  saveInvestingExperienceMoral,
  saveEconomicSituation,
  saveEconomicSituationMoral,
  saveInvestingGoals,
  saveInvestingGoalsMoral,
  saveProfileAuth as saveProfileAuthRequest,
} from 'api/client';

import ProfilingSaveSteps from './context';
import type {
  EconomicSituationMoValues,
  EconomicSituationValues,
  InvestingExperienceMoValues,
  InvestingExperienceValues,
  InvestingGoalsMoValues,
  InvestingGoalsValues,
  ProfileValues,
} from './models';
import { ProfileModal } from './types';

const initialState = {
  saved: false,
};

interface Props {
  children: React.ReactNode;
}

const ProfilingSaveStepsProvider = ({ children }: Props) => {
  const { currentCustomer, updateCurrentCustomer } =
    useContext(CurrentCustomer);
  const { dataStepPersonal } = useContext(OnboardingSaveSteps);
  const [investingExperience, setInvestingExperience] =
    useState<InvestingExperienceValues>(initialState);
  const [economicSituation, setEconomicSituation] =
    useState<EconomicSituationValues>(initialState);
  const [investingGoals, setInvestingGoals] =
    useState<InvestingGoalsValues>(initialState);
  const [profilingResult, setProfilingResult] = useState<any>({
    profileDescription: '',
    profileId: null,
    profilingPoints: null,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [showModalProfile, setShowModalProfile] = useState<boolean>(false);
  const [dataProfile, setDataProfile] = useState<ProfileModal | null>(null);

  const handleCloseModalProfile = () => {
    setShowModalProfile(false);
    setDataProfile(null);
  };
  const handleOpenModalProfile = (data: ProfileModal | null) => {
    setShowModalProfile(true);
    setDataProfile(data);
  };

  const resetProfiling = () => {
    setInvestingExperience(initialState);
    setEconomicSituation(initialState);
    setInvestingGoals(initialState);
  };

  const investingExperienceRemapper = (
    finalValues: InvestingExperienceValues,
  ) => {
    return {
      // registrationDate: 'string',
      updateDate: new Date(),
      // rfc: 'string',
      clientId: finalValues.clientId,
      age: finalValues.age?.value,
      academicLevel: finalValues.academicLevel?.value,
      currentOccupation: finalValues.currentOccupation?.value,
      experienceInStock: finalValues.experienceInStock || null,
      experienceInBonus: finalValues.experienceInBonus || null,
      experienceInCetes: finalValues.experienceInCetes || null,
      experienceInFutures: finalValues.experienceInFutures || null,
      termInStock: finalValues.termInStock?.value,
      amountInStock: finalValues.amountInStock?.value,
      termInBonus: finalValues.termInBonus?.value,
      amountInBonus: finalValues.amountInBonus?.value,
      termInCetes: finalValues.termInCetes?.value,
      amountInCetes: finalValues.amountInCetes?.value,
      termInFutures: finalValues.termInFutures?.value,
      amountInFutures: finalValues.amountInFutures?.value,
      knowledgePortfolioManagement:
        finalValues.knowledgePortfolioManagement || null,
      knowledgeInvestmentRecommendation:
        finalValues.knowledgeInvestmentRecommendation || null,
      servicePortfolioManagement:
        finalValues.servicePortfolioManagement || null,
      serviceInvestmentRecommendation:
        finalValues.serviceInvestmentRecommendation || null,
      conceptAnnualFee: finalValues.conceptAnnualFee || null,
      conceptPresentValue: finalValues.conceptPresentValue || null,
      conceptVolatility: finalValues.conceptVolatility || null,
      conceptDuration: finalValues.conceptDuration || null,
      conceptStockPrice: finalValues.conceptStockPrice || null,
      creditRisk: finalValues.creditRisk || null,
      systemicRisk: finalValues.systemicRisk || null,
    };
  };

  const economicSituationRemapper = (finalValues: EconomicSituationValues) => {
    // eslint-disable-next-line
    const { saved, ...values } = finalValues;
    return {
      ...values,
      updateDate: new Date(),
      investingResourceOrigin: values.investingResourceOrigin?.map(
        (i: any) => i.value,
      ),
      institutionInvestingResourcePercentage:
        values.institutionInvestingResourcePercentage?.value,
      patrimonyInvestingResourcePercentage:
        values.patrimonyInvestingResourcePercentage?.value,
      totalInvestingPercentage: values.totalInvestingPercentage?.value,
      totalExpenseInvestingPercentage:
        values.totalExpenseInvestingPercentage?.value,
      monthlyIncomeRange: values.monthlyIncomeRange?.value,
      futureIncomePrediction: values.futureIncomePrediction?.value,
      dependantNumber: values.dependantNumber?.value,
    };
  };

  const investingGoalsRemapper = (finalValues: InvestingGoalsValues) => {
    // eslint-disable-next-line
    const { saved, ...values } = finalValues;
    return {
      ...values,
      investingGoal: values.investingGoal?.value || values.investingGoal,
      incomeGenerated: values.incomeGenerated?.value || values.incomeGenerated,
      liquidityPercentage:
        values.liquidityPercentage?.value || values.liquidityPercentage,
      returnsEstimation:
        values.returnsEstimation?.value || values.returnsEstimation,
      riskEarning: values.riskEarning,
    };
  };

  const moralPersonStepsRemapper = (values: InvestingExperienceMoValues) => {
    const remappedValues: any = {};

    Object.entries(values).forEach(
      ([key, value]) => (remappedValues[key] = value || ''),
    );

    return remappedValues;
  };

  const economicSituationMoralPersonStepsRemapper = (
    values: EconomicSituationMoValues,
  ) => {
    let remappedValues: any = {};

    Object.entries(values).forEach(
      ([key, value]) => (remappedValues[key] = value || ''),
    );

    remappedValues = {
      ...remappedValues,
      treasuryPercentage: remappedValues.treasuryPercentage || '0',
      projectsPercentage: remappedValues.projectsPercentage || '0',
      savingsPercentage: remappedValues.savingsPercentage || '0',
      heritajePercentage: remappedValues.heritajePercentage || '0',
    };

    return remappedValues;
  };

  const investingGoalsMoralPersonStepsRemapper = (
    values: InvestingGoalsMoValues,
  ) => {
    let remappedValues: any = {};

    Object.entries(values).forEach(
      ([key, value]) => (remappedValues[key] = value || ''),
    );

    remappedValues = {
      ...remappedValues,
      incomeCapitalPercentage: remappedValues.incomeCapitalPercentage || '0',
      preserveHeritagePercentage:
        remappedValues.preserveHeritagePercentage || '0',
      increaseHeritagePercentage:
        remappedValues.increaseHeritagePercentage || '0',
    };

    return remappedValues;
  };

  const profileRemapper = (values: ProfileValues) => {
    const clientId =
      dataStepPersonal.clientId ||
      values.clientId ||
      currentCustomer.clientId ||
      null;

    return {
      clientId: Number(clientId),
      recommendedProfile: values?.profile?.value,
      authorizedProfile: values?.profileAuth?.value,
      authorizationProfileDate: formatDate(
        values.profileAuthDate || new Date(),
        'yyyy-MM-dd',
      ),
    };
  };

  const saveProfiling = async () => {
    const investingGoals2: any = investingGoals;
    const investingGoalsValues: any = investingGoalsRemapper(
      investingGoals2.values ? investingGoals2.values : investingGoals,
    );

    const clientId =
      dataStepPersonal.clientId ||
      investingGoalsValues.clientId ||
      currentCustomer.clientId ||
      null;

    const { data, success } = await saveInvestingGoals({
      clientId,
      ...investingGoalsValues,
    });

    if (success) {
      updateCurrentCustomer({
        profileId: data?.profileId,
        profileDescription: data?.profileDescription,
        profilingComplete: true,
        clientRole:
          currentCustomer.clientRole || currentCustomer.generalData?.clientRole,
      } as DataCurrentCustomerType);
      setProfilingResult(data);
      return { success };
    }

    return null;
  };

  const saveStepInvestingExperience = (
    invExperience: InvestingExperienceValues,
  ) => {
    const clientId =
      dataStepPersonal.clientId ||
      invExperience.clientId ||
      currentCustomer.clientId ||
      null;
    const investingExperienceValues = investingExperienceRemapper({
      clientId: clientId || 0,
      ...invExperience,
    });

    return saveInvestingExperience(investingExperienceValues);
  };

  const saveStepInvestingExperienceMoralPerson = (
    values: InvestingExperienceMoValues,
  ) => {
    const remappedValues = moralPersonStepsRemapper(values);

    const clientId =
      dataStepPersonal.clientId ||
      remappedValues.clientId ||
      currentCustomer.clientId ||
      null;

    const investingExperienceValues = {
      clientId,
      rfc: dataStepPersonal.rfc || null,
      ...remappedValues,
      companyTimeManagingContracts:
        remappedValues.companyTimeManagingContracts.value ||
        remappedValues.companyTimeManagingContracts,
      portfolioService: !!remappedValues.portfolioService,
      recommendationsService: !!remappedValues.recommendationsService,
    };

    return saveInvestingExperienceMoral(investingExperienceValues);
  };

  const saveStepEconomicSituation = (ecoSituation: EconomicSituationValues) => {
    const economicSituationValues = economicSituationRemapper(ecoSituation);

    const clientId =
      dataStepPersonal.clientId ||
      economicSituationValues.clientId ||
      currentCustomer.clientId ||
      null;

    return saveEconomicSituation({
      clientId,
      ...economicSituationValues,
    });
  };

  const saveStepEconomicSituationMoralPerson = (
    values: EconomicSituationMoValues,
  ) => {
    const remappedValues = economicSituationMoralPersonStepsRemapper(values);

    const clientId =
      dataStepPersonal.clientId ||
      remappedValues.clientId ||
      currentCustomer.clientId ||
      null;

    return saveEconomicSituationMoral({
      clientId,
      rfc: dataStepPersonal.rfc || null,
      ...remappedValues,
    });
  };

  const saveStepInvestingGoals = (invGoals: InvestingGoalsValues) => {
    const investingGoalsValues = investingGoalsRemapper(invGoals);
    const clientId =
      dataStepPersonal.clientId ||
      investingGoalsValues.clientId ||
      currentCustomer.clientId ||
      null;
    return saveInvestingGoals({
      ...investingGoalsValues,
      clientId,
      rfc: dataStepPersonal.rfc || null,
    });
  };

  const saveStepInvestingGoalsMoralPerson = (
    values: InvestingGoalsMoValues,
  ) => {
    const remappedValues = investingGoalsMoralPersonStepsRemapper(values);

    const clientId =
      dataStepPersonal.clientId ||
      remappedValues.clientId ||
      currentCustomer.clientId ||
      null;

    return saveInvestingGoalsMoral({
      clientId,
      rfc: dataStepPersonal.rfc || null,
      ...remappedValues,
    });
  };

  const saveProfileAuth = (values: ProfileValues) => {
    const remappedValues = profileRemapper(values);

    return saveProfileAuthRequest(remappedValues);
  };

  return (
    <ProfilingSaveSteps.Provider
      value={{
        investingExperience,
        economicSituation,
        investingGoals,
        profilingResult,
        loading,
        saveProfiling,
        resetProfiling,
        setInvestingExperience,
        setEconomicSituation,
        setInvestingGoals,
        saveStepInvestingExperience,
        saveStepEconomicSituation,
        saveStepInvestingGoals,
        setLoading,
        saveStepInvestingExperienceMoralPerson,
        saveStepEconomicSituationMoralPerson,
        saveStepInvestingGoalsMoralPerson,
        handleCloseModalProfile,
        showModalProfile,
        dataProfile,
        handleOpenModalProfile,
        saveProfileAuth,
      }}
    >
      {children}
    </ProfilingSaveSteps.Provider>
  );
};

export default ProfilingSaveStepsProvider;
